import { FilterAlt } from "@mui/icons-material";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme, styled } from "styled-components";
import { components } from "../../api/types";
import DashedArrow from "../../assets/icons/dashed_arrow.svg";
import DashedArrowDown from "../../assets/icons/dashed_arrow_down.svg";
import Button from "../../components/buttons/Button";
import DotsMenu from "../../components/buttons/DotsMenu";
import TextInput from "../../components/inputs/TextInput";
import Dialog from "../../components/misc/Dialog";
import { useApi } from "../../contexts/ApiContext";
import { useAppContext } from "../../contexts/AppContext";
import { useError } from "../../contexts/ErrorContext";
import Translations from "../../lang/en/pages.json";
import Main from "../../layouts/Main";
import { CasesMockResponse } from "../../onboarding/mock";
import { formatDate } from "../../utils";
import { caseStatusColorMap, literalCaseStatus } from "./types";
import { Icon } from "@iconify/react";

type CaseStatus = components["schemas"]["CaseStatus"];
type CaseMeta = components["schemas"]["CaseMeta"];

const EmptyState: React.FC<{
  title: string;
  description: string;
}> = ({ title, description }) => (
  <Stack spacing={0} textAlign={"center"} sx={{ position: "relative" }}>
    <Typography color="#000000" fontWeight={"bold"} fontSize={18}>
      {title}
    </Typography>
    <Typography color="#000000" fontWeight={"light"} fontSize={16}>
      {description}
    </Typography>
    <Box
      sx={{
        position: "absolute",
        right: "64%",
        bottom: "-56%",
      }}
    >
      <img src={DashedArrowDown} />
    </Box>
  </Stack>
);

const CaseArchiveDialog: React.FC<{
  caseInfo: CaseMeta;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}> = ({ caseInfo, open, onClose, onConfirm, isLoading = false }) => {
  return (
    <Dialog
      minHeight={"none"}
      fullWidth
      showClose={false}
      title={Translations.pages.cases_page.dialogs.archive.title}
      open={open}
      onClose={onClose}
    >
      <Typography mb={2} textAlign={"center"} variant="h6">
        {Translations.pages.cases_page.dialogs.archive.title}
      </Typography>
      <Stack spacing={4}>
        <Typography variant="body2">
          {Translations.pages.cases_page.dialogs.archive.message}
        </Typography>
        <Stack direction={"row"} spacing={2} justifyContent={"flex-end"}>
          <Button width={"auto"} onClick={onClose} variant="text">
            {Translations.pages.cases_page.buttons.cancel}
          </Button>
          <Button
            width={"auto"}
            disabled={isLoading}
            onClick={onConfirm}
            variant="green"
          >
            {isLoading ? (
              <CircularProgress size={20} sx={{ color: "#ffffff" }} />
            ) : (
              Translations.pages.cases_page.buttons.archive
            )}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};

const INVALID_CHARS_RE = /[^\w .,\-'():#@&]/;

const CaseRenameDialog: React.FC<{
  caseInfo: CaseMeta;
  open: boolean;
  onClose: () => void;
  onConfirm: (newName: string) => void;
  isLoading?: boolean;
}> = ({ caseInfo, open, onClose, onConfirm, isLoading = false }) => {
  const [newName, setNewName] = React.useState<string>(caseInfo.name);

  useEffect(() => {
    setNewName(caseInfo.name);
  }, [caseInfo]);

  const hasInvalidChars = INVALID_CHARS_RE.test(newName);

  return (
    <Dialog
      fullWidth
      maxWidth={480}
      showClose={false}
      title={Translations.pages.cases_page.dialogs.rename.title}
      open={open}
      onClose={onClose}
      minHeight={"none"}
    >
      <Typography mb={2} textAlign={"center"} variant="h6">
        {Translations.pages.cases_page.dialogs.rename.title}
      </Typography>
      <Stack spacing={4}>
        <TextInput
          sx={{
            "& .MuiInputBase-input": {
              backgroundColor: "white",
              border: "1px solid #ccc",
            },
          }}
          fullWidth
          label={Translations.pages.cases_page.dialogs.rename.field.label}
          placeholder={
            Translations.pages.cases_page.dialogs.rename.field.placeholder
          }
          value={newName}
          required
          error={hasInvalidChars}
          helperText={
            hasInvalidChars
              ? Translations.pages.cases_page.errors.rename_invalid_name
              : " "
          }
          onChange={(newName: string | number) => setNewName(newName as string)}
        />
        <Stack direction={"row"} spacing={2} justifyContent={"flex-end"}>
          <Button
            width={"auto"}
            disabled={isLoading || newName.length === 0 || hasInvalidChars}
            onClick={() => onConfirm(newName)}
            variant="green"
          >
            {isLoading ? (
              <CircularProgress size={20} sx={{ color: "#ffffff" }} />
            ) : (
              Translations.pages.cases_page.buttons.change
            )}
          </Button>
          <Button
            disabled={isLoading}
            width={"auto"}
            onClick={onClose}
            variant="text"
          >
            {Translations.pages.cases_page.buttons.close}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};

const Cases: React.FC = () => {
  const navigate = useNavigate();
  const { setBreadcrumbs, isLoading, setIsLoading } = useAppContext();
  const [cases, setCases] = React.useState<CaseMeta[] | null>(null);
  const [caseRenameDialogOpen, setCaseRenameDialogOpen] = React.useState(false);
  const [selectedCase, setSelectedCase] = React.useState<CaseMeta | null>(null);
  const [filteredCases, setFilteredCases] = React.useState<CaseMeta[]>([]);
  const [statusFilter, setStatusFilter] = React.useState<string[]>([]);
  const [caseArchiveDialogOpen, setCaseArchiveDialogOpen] =
    React.useState(false);

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { client } = useApi();
  const { showSuccess, showError } = useError();
  const theme = useTheme();

  const fetchCases = async () => {
    // setIsLoading(true);

    const response = await client.GET("/v1/cases/", { cacheable: true });
    try {
      setCases(response.data as CaseMeta[]);
    } catch (error) {
      setCases([]);
      console.error("Error fetching cases", error);
    } finally {
      // setIsLoading(false);
    }
  };
  useEffect(() => {
    setBreadcrumbs([]);
    fetchCases();
  }, []);

  useEffect(() => {
    const filtered = (cases ?? []).filter((c) =>
      statusFilter.length === 0
        ? true
        : statusFilter.includes(c.status as string)
    );
    // filtered.sort((a, b) => {
    //   return a.status === "archived" ? 1 : -1;
    // });

    setFilteredCases(filtered);
  }, [statusFilter, cases]);

  const handleRename = (c: CaseMeta) => {
    console.log("Renaming case", c);
    setSelectedCase(c);
    setTimeout(() => {
      setCaseRenameDialogOpen(true);
    }, 100);
  };

  const handleArchive = async (
    action: "archive" | "unarchive",
    caseMeta: CaseMeta
  ) => {
    try {
      await client.PATCH(`/v1/cases/{case_id}`, {
        params: {
          path: {
            case_id: caseMeta?.id,
          },
        },
        body: {
          status: action === "archive" ? "archived" : "active",
        },
      });
      await refetchCase();

      showSuccess(Translations.pages.cases_page.messages.case_archived);
    } catch (error) {
      showError(Translations.pages.cases_page.errors[action]);
    } finally {
      setIsSubmitting(false);
    }

    setCaseArchiveDialogOpen(false);
  };

  const refetchCase = async () => {
    setIsSubmitting(true);
    try {
      const response = await client.GET("/v1/cases/", { cacheable: false });
      for (const c of response.data as CaseMeta[]) {
        if (c.id === selectedCase?.id) {
          setCases((prev) =>
            (prev ?? []).map((caseItem) =>
              caseItem.id === c.id ? { ...caseItem, ...c } : caseItem
            )
          );
          break;
        }
      }
    } catch (error) {
      console.error("Error refetching case", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRenameSave = async (newName: string) => {
    if (!selectedCase) return;

    try {
      await client.PATCH(`/v1/cases/{case_id}`, {
        params: {
          path: {
            case_id: selectedCase.id,
          },
        },
        body: {
          name: newName,
        },
      });

      await refetchCase();
    } catch (error) {
      console.error("Error renaming case", error);
      showError(Translations.pages.cases_page.errors.rename);
    }

    setCaseRenameDialogOpen(false);
    showSuccess(Translations.pages.cases_page.messages.case_renamed);
  };

  interface NewCaseButtonProps {
    newCaseClick: (type: "single" | "multiple") => void;
  }

  const NewCaseStyledButton = styled(Button)({
    paddingY: 3,
    height: 38,
    maxWidth: 200,
    boxShadow:
      "0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20)",
  });

  const NewCaseButton: React.FC<NewCaseButtonProps> = React.memo(
    ({ newCaseClick }) => (
      <Card sx={{ backgroundColor: "rgba(0, 0, 0, 0.04)", padding: 4 }}>
        <Stack spacing={4} textAlign={"center"}>
          <Typography variant="h6">
            {Translations.pages.cases_page.add_new_case.title}
          </Typography>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={2}
            justifyContent={"center"}
          >
            <NewCaseStyledButton
              onClick={() => newCaseClick("single")}
              variant="green"
            >
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <Typography
                  variant="body2"
                  fontWeight={"medium"}
                  textTransform={"uppercase"}
                >
                  {Translations.pages.cases_page.add_new_case.buttons.single}
                </Typography>
                <Icon
                  icon="material-symbols:upload-file-outline"
                  width="24"
                  height="24"
                />
              </Stack>
            </NewCaseStyledButton>
            <NewCaseStyledButton
              onClick={() => newCaseClick("multiple")}
              variant={"purple"}
            >
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <Typography
                  variant="body2"
                  fontWeight={"medium"}
                  textTransform={"uppercase"}
                >
                  {Translations.pages.cases_page.add_new_case.buttons.multiple}
                </Typography>
                <Icon icon="material-symbols:layers" width="24" height="24" />
              </Stack>
            </NewCaseStyledButton>
          </Stack>
        </Stack>
      </Card>
    )
  );

  const CaseRow: React.FC<{
    caseInfo: CaseMeta;
    isExample?: boolean;
    renameClick: () => void;
    archiveClick: (action: "archive" | "unarchive") => void;
  }> = ({ caseInfo, renameClick, archiveClick, isExample }) => {
    const sx: any = { backgroundColor: isExample ? "#7C4DFF14" : "inherit" };
    if (caseInfo.status === "archived") {
      sx.opacity = 0.5;
    } else {
      sx["&:hover"] = {
        backgroundColor: theme.palette.grey[100],
      };
      sx.cursor = "pointer";
    }
    if (isExample) {
      sx.backgroundColor = "#7C4DFF14";
    }

    return (
      <Stack
        px={2}
        width={"100%"}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        onClick={() =>
          navigate(
            isExample
              ? `/cases/${caseInfo.id}/files`
              : `/cases/${caseInfo.id}/dashboard/overview`
          )
        }
        padding={2}
        mt={0}
        borderBottom={"1px solid #404943"}
        sx={sx}
      >
        <Stack spacing={1} width={"80%"}>
          <Typography variant="body2" fontWeight={"medium"}>
            {caseInfo.name}
          </Typography>
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <Typography width={"30%"} variant="body2" fontWeight={"light"}>
              {formatDate(caseInfo.created_at)}
            </Typography>
            {caseInfo.updated_at && (
              <Stack
                width={"100%"}
                direction={"row"}
                spacing={1}
                alignItems={"center"}
              >
                <Divider
                  sx={{ height: 20, width: "2px", borderColor: "#171D19" }}
                  orientation="vertical"
                />
                <Stack pl={2} direction={"row"} spacing={1}>
                  <Typography pr={0} variant="body2" fontWeight={"bold"}>
                    {Translations.pages.cases_page.table.columns.updated.label}:
                  </Typography>
                  <Typography variant="body2" fontWeight={"light"}>
                    {formatDate(caseInfo.updated_at)}
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Stack>
        <Stack
          justifyContent={"flex-end"}
          alignItems={"center"}
          width="40%"
          direction={"row"}
          spacing={2}
        >
          <Chip
            size="small"
            label={isExample ? "Demo Case" : literalCaseStatus(caseInfo.status)}
            style={{
              textTransform: "capitalize",
              backgroundColor:
                caseStatusColorMap[caseInfo.status as CaseStatus].bg,
              color: caseStatusColorMap[caseInfo.status as CaseStatus].text,
              borderRadius: 15,
            }}
          />
          <DotsMenu
            onChange={(value) => {
              if (value.toString() === "rename") {
                renameClick();
              } else {
                archiveClick(value.toString() as "archive" | "unarchive");
              }
            }}
            options={[
              {
                label: Translations.pages.cases_page.buttons.rename,
                value: "rename",
              },
              {
                label:
                  caseInfo.status === "archived"
                    ? Translations.pages.cases_page.buttons.unarchive
                    : Translations.pages.cases_page.buttons.archive,
                value: caseInfo.status === "archived" ? "unarchive" : "archive",
                disabled: !["active", "archived", "error"].includes(
                  caseInfo.status
                ),
              },
            ]}
          />
        </Stack>
      </Stack>
    );
  };

  const firstTimer = useMemo(() => {
    return cases?.length === 0;
  }, [cases]);

  if (isLoading || cases === null) {
    return (
      <Main hidePages>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <CircularProgress />
        </Box>
      </Main>
    );
  }

  const handleNewCaseClick = (type: "single" | "multiple") => {
    if (type === "single") {
      navigate("/cases/create");
    } else {
      navigate("/cases/create/bulk");
    }
  };

  return (
    <Main hidePages>
      <Stack gap={8}>
        {firstTimer ? (
          <Card sx={{ position: "relative", padding: 4 }}>
            <Stack
              justifyContent={"center"}
              alignItems={"center"}
              spacing={0.6}
              width={"100%"}
            >
              <NewCaseButton newCaseClick={handleNewCaseClick} />

              <Typography
                pt={2}
                fontWeight={"bold"}
                variant="body2"
                fontSize={18}
              >
                {Translations.pages.cases_page.empty_new_case.title}
              </Typography>
              <Typography color="#000000" fontWeight={"light"} fontSize={16}>
                {Translations.pages.cases_page.empty_new_case.description}
              </Typography>
              <Box
                sx={{
                  position: "absolute",
                  right: "65%",
                  bottom: "30%",
                }}
              >
                <img src={DashedArrow} />
              </Box>
            </Stack>
          </Card>
        ) : (
          <NewCaseButton newCaseClick={handleNewCaseClick} />
        )}

        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Box>
            <Typography variant="body2">
              {Translations.pages.cases_page.page_label}
            </Typography>
          </Box>
          <Divider sx={{ width: "80%" }} orientation="horizontal" />
          <Box>
            <DotsMenu
              showCheckbox
              options={Object.keys(caseStatusColorMap).map((status) => ({
                label: status,
                value: status,
              }))}
              onChange={(value) => setStatusFilter(value as string[])}
              value={statusFilter}
              icon={<FilterAlt />}
            />
          </Box>
        </Stack>

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Box>
        ) : (
          cases?.length === 0 && (
            <EmptyState {...Translations.pages.cases_page.example} />
          )
        )}
        <Stack spacing={2}>
          {(firstTimer ? CasesMockResponse : filteredCases)?.map((c) => (
            <CaseRow
              key={c.id}
              caseInfo={c}
              isExample={firstTimer}
              renameClick={() => handleRename(c)}
              archiveClick={(action: "archive" | "unarchive") => {
                handleArchive(action, c);
              }}
            />
          ))}
        </Stack>
      </Stack>
      {selectedCase && (
        <CaseRenameDialog
          open={caseRenameDialogOpen}
          isLoading={isSubmitting}
          caseInfo={selectedCase}
          onClose={() => setCaseRenameDialogOpen(false)}
          onConfirm={(newName: string) => {
            setIsSubmitting(true);
            handleRenameSave(newName);
          }}
        />
      )}
      {selectedCase && (
        <CaseArchiveDialog
          isLoading={isSubmitting}
          open={caseArchiveDialogOpen}
          caseInfo={selectedCase}
          onClose={() => setCaseArchiveDialogOpen(false)}
          onConfirm={() => {
            setIsSubmitting(true);
            handleArchive(
              selectedCase.status === "archived" ? "unarchive" : "archive",
              selectedCase
            );
          }}
        />
      )}
    </Main>
  );
};

export default Cases;
